import React, { useState } from 'react';
import styles from './QuoteBlock.module.scss';
import { QuoteBlockFragment } from '@src/graphql/gql-types';
import { Container } from '@src/elements/Grid';
import Heading from '@src/elements/Heading';
import { Carousel } from 'react-responsive-carousel';
import MediaImage from '../MediaImage';
import MediaVideo from '../MediaVideo';
import CTAButton from '../Button/CTAButton';
import { COLOR_OUTERSPACE, COLOR_WHITE } from '@src/types/colors';
import { ButtonIcon } from '../Button';
import Visibility from '@src/elements/Visibility';
import { ContentBlockBackgroundColors } from '@src/types/colors';
import classNames from 'classnames';
import { getColorClasses } from '@src/utils/colorClasses';

type QuoteBlock = QuoteBlockFragment & {
  backgroundColor?: ContentBlockBackgroundColors;
};

const QuoteBlock: React.FC<QuoteBlock> = ({
  tagline,
  heading,
  description,
  mediaItem,
  quoteItemsCollection,
  primaryCta,
  backgroundColor
}) => {
  const [selected, setSelected] = useState<number>(0);

  const previousQuote = () => {
    if (selected === 0) {
      setSelected((quoteItemsCollection?.items?.length || 0) - 1);
    } else {
      setSelected(selected - 1);
    }
  };

  const nextQuote = () => {
    if (selected === (quoteItemsCollection?.items?.length || 0) - 1) {
      setSelected(0);
    } else {
      setSelected(selected + 1);
    }
  };
  return (
    <div
      className={classNames(
        styles.quoteBlock,
        backgroundColor ? getColorClasses(backgroundColor, styles) : ''
      )}
    >
      <Container containerWidth="fluid">
        <div className={styles.quoteBlockContainer}>
          <div className={styles.content}>
            <div className={styles.tagline}>{tagline}</div>
            <Heading priority={2} fontStyle="h1">
              {heading}
            </Heading>
            <p className={styles.description}>{description}</p>
            <Carousel
              showArrows={false}
              showStatus={false}
              showIndicators={false}
              preventMovementUntilSwipeScrollTolerance={true}
              swipeScrollTolerance={48}
              infiniteLoop={true}
              showThumbs={false}
              selectedItem={selected}
              onChange={setSelected}
              autoPlay={true}
              interval={4000}
            >
              {quoteItemsCollection?.items.map((item) => (
                <div
                  key={`${item?.name}-${item?.title}`}
                  className={styles.quoteItem}
                >
                  <blockquote className={styles.quote}>
                    {item?.quote}
                  </blockquote>
                  <div className={styles.attribution}>
                    <p className={styles.name}>{item?.name}</p>
                    <p className={styles.title}>{item?.title}</p>
                  </div>
                </div>
              ))}
            </Carousel>
            {quoteItemsCollection && quoteItemsCollection?.items.length > 2 && (
              <div className={styles.buttonContainerWrapper}>
                <div className={styles.buttonContainer}>
                  <ButtonIcon
                    boxIcon="left-arrow-alt"
                    color={COLOR_WHITE}
                    fillVariant="filled"
                    onClick={previousQuote}
                    aria-label="Previous Quote"
                  />
                  <ButtonIcon
                    boxIcon="right-arrow-alt"
                    color={COLOR_WHITE}
                    fillVariant="filled"
                    onClick={nextQuote}
                    aria-label="Next Quote"
                  />
                </div>
              </div>
            )}
            {primaryCta && (
              <Visibility xs={false} md={true}>
                <div className={styles.ctaContainer}>
                  <CTAButton
                    cta={primaryCta}
                    color={COLOR_OUTERSPACE}
                    fillVariant="filled"
                    label={primaryCta.label || 'Find out more'}
                  />
                </div>
              </Visibility>
            )}
          </div>
          {mediaItem && (
            <div className={styles.mediaItem}>
              {mediaItem?.__typename === 'MediaImage' && (
                <div className={styles.mediaImageContainer}>
                  <MediaImage {...mediaItem} />
                </div>
              )}
              {mediaItem?.__typename === 'MediaVideo' && (
                <div className={styles.mediaVideoContainer}>
                  <MediaVideo {...mediaItem} fillParent={true} />
                </div>
              )}
            </div>
          )}
          {primaryCta && (
            <Visibility xs={true} md={false}>
              <div className={styles.ctaContainer}>
                <CTAButton
                  cta={primaryCta}
                  color={COLOR_OUTERSPACE}
                  fillVariant="filled"
                  label={primaryCta.label || 'Find out more'}
                />
              </div>
            </Visibility>
          )}
        </div>
      </Container>
    </div>
  );
};

export default QuoteBlock;
